import { useEffect, useState } from 'react';
import { NavLink, Link, Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAdminNotifications } from '@/hooks/useAdminNotifications';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
  superAdminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/admin', label: 'Overview', icon: 'ri-dashboard-line', end: true },
  { to: '/admin/settings', label: 'Site Settings', icon: 'ri-settings-3-line' },
  { to: '/admin/pages', label: 'Pages', icon: 'ri-pages-line' },
  { to: '/admin/books', label: 'Books', icon: 'ri-book-2-line' },
  { to: '/admin/leaders', label: 'Leadership', icon: 'ri-user-star-line' },
  { to: '/admin/sermons', label: 'Sermons', icon: 'ri-mic-line' },
  { to: '/admin/live', label: 'Live Stream', icon: 'ri-live-line' },
  { to: '/admin/events', label: 'Events', icon: 'ri-calendar-event-line' },
  { to: '/admin/news', label: 'News & Blog', icon: 'ri-newspaper-line' },
  { to: '/admin/gallery', label: 'Photo Gallery', icon: 'ri-image-line' },
  { to: '/admin/testimonials', label: 'Testimonials', icon: 'ri-chat-quote-line' },
  { to: '/admin/ministries', label: 'Ministries', icon: 'ri-heart-3-line' },
  { to: '/admin/pureheart', label: 'PureHeart Program', icon: 'ri-heart-line' },
  { to: '/admin/inbox', label: 'Inbox & Requests', icon: 'ri-inbox-line' },
  { to: '/admin/users', label: 'Team & Admins', icon: 'ri-team-line', superAdminOnly: true },
  { to: '/admin/test-email', label: 'Test Email', icon: 'ri-mail-send-line', superAdminOnly: true },
];

export default function AdminLayout() {
  const { user, loading, isAdmin, isSuperAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const location = useLocation();
  const { counts, reload: reloadNotifications } = useAdminNotifications();

  useEffect(() => {
    setBellOpen(false);
    reloadNotifications();
  }, [location.pathname, reloadNotifications]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500 inline-block" aria-hidden="true" />
          <p className="mt-3 text-sm text-foreground-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user || !isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-background-100 flex">
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-foreground-950 text-white flex flex-col transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-5 border-b border-foreground-800">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-xs font-bold">
              TG
            </span>
            <div>
              <div className="font-heading font-bold text-sm leading-tight">TGMCI Admin</div>
              <div className="text-[10px] text-foreground-400">Content Manager</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1.5 rounded-md hover:bg-foreground-800 cursor-pointer"
            aria-label="Close menu"
          >
            <i className="ri-close-line" aria-hidden="true" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV_ITEMS.filter((item) => !item.superAdminOnly || isSuperAdmin).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-label transition-colors whitespace-nowrap ${
                  isActive
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-300 hover:bg-foreground-800 hover:text-white'
                }`
              }
            >
              <i className={`${item.icon} text-base`} aria-hidden="true" />
              {item.label}
              {item.to === '/admin/inbox' && counts.total > 0 && (
                <span className="ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-accent-500 text-foreground-950 text-[10px] font-bold">
                  {counts.total}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-foreground-800 space-y-2">
          <Link
            to="/"
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-foreground-300 hover:bg-foreground-800 hover:text-white transition-colors"
          >
            <i className="ri-external-link-line" aria-hidden="true" />
            View Website
          </Link>
          <LogoutButton />
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-background-200 px-4 md:px-8 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md hover:bg-background-100 cursor-pointer"
              aria-label="Open menu"
            >
              <i className="ri-menu-line text-xl" aria-hidden="true" />
            </button>
            <div>
              <h1 className="font-heading font-semibold text-lg text-foreground-900 leading-tight">
                {currentTitle(location.pathname)}
              </h1>
              <p className="text-xs text-foreground-500">Manage your church website content</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setBellOpen((o) => !o)}
                className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
                aria-label="Notifications"
              >
                <i className="ri-notification-3-line text-lg text-foreground-600" aria-hidden="true" />
                {counts.total > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-accent-500 text-foreground-950 text-[10px] font-bold">
                    {counts.total}
                  </span>
                )}
              </button>

              {bellOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setBellOpen(false)} aria-hidden="true" />
                  <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg border border-background-200 shadow-lg z-40 overflow-hidden">
                    <div className="px-4 py-3 border-b border-background-100">
                      <p className="font-heading font-semibold text-sm text-foreground-900">Notifications</p>
                    </div>
                    {counts.total === 0 ? (
                      <p className="px-4 py-6 text-sm text-foreground-500 text-center">You&apos;re all caught up.</p>
                    ) : (
                      <div className="divide-y divide-background-100">
                        {counts.prayer > 0 && (
                          <NotifRow to="/admin/inbox" icon="ri-hand-heart-line" label={`${counts.prayer} new prayer request${counts.prayer > 1 ? 's' : ''}`} onNavigate={() => setBellOpen(false)} />
                        )}
                        {counts.donations > 0 && (
                          <NotifRow to="/admin/inbox" icon="ri-heart-3-line" label={`${counts.donations} new donation${counts.donations > 1 ? 's' : ''}`} onNavigate={() => setBellOpen(false)} />
                        )}
                        {counts.contact > 0 && (
                          <NotifRow to="/admin/inbox" icon="ri-mail-line" label={`${counts.contact} new message${counts.contact > 1 ? 's' : ''}`} onNavigate={() => setBellOpen(false)} />
                        )}
                      </div>
                    )}
                    <Link
                      to="/admin/inbox"
                      onClick={() => setBellOpen(false)}
                      className="block px-4 py-3 text-center text-xs font-label font-medium text-primary-600 hover:bg-background-50 border-t border-background-100"
                    >
                      Open Inbox
                    </Link>
                  </div>
                </>
              )}
            </div>
            <span className="hidden md:inline text-sm text-foreground-600">
              {user.email}
            </span>
            <span className="w-8 h-8 rounded-full bg-accent-500 text-foreground-950 flex items-center justify-center text-xs font-bold">
              {(user.email?.[0] ?? 'A').toUpperCase()}
            </span>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NotifRow({
  to,
  icon,
  label,
  onNavigate,
}: {
  to: string;
  icon: string;
  label: string;
  onNavigate: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className="flex items-center gap-3 px-4 py-3 hover:bg-background-50 transition-colors"
    >
      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
        <i className={icon} aria-hidden="true" />
      </span>
      <span className="text-sm text-foreground-700">{label}</span>
    </Link>
  );
}

function LogoutButton() {
  const { signOut } = useAuth();
  return (
    <button
      type="button"
      onClick={signOut}
      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
    >
      <i className="ri-logout-box-r-line" aria-hidden="true" />
      Sign Out
    </button>
  );
}

function currentTitle(pathname: string): string {
  const item = NAV_ITEMS.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)));
  return item ? item.label : 'Dashboard';
}